import { supabase } from './supabase';

export const tracker = {
  async trackScoreView(token: string) {
    try {
      
      // Vérifier d'abord si le prospect existe avec ce token
      const { data: prospect, error: checkError } = await supabase
        .from('prospects')
        .select('id, token, page_vue')
        .eq('token', token)
        .single();
      
      if (checkError) {
        console.error('Erreur lors de la vérification du token pour score view:', checkError);
        return null;
      }
      
      if (!prospect) {
        console.error('Aucun prospect trouvé avec ce token pour score view:', token);
        return null;
      }
      
      
      // Mise à jour avec date actuelle
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('prospects')
        .update({ 
          page_vue: true, 
          updated_at: now
        })
        .eq('token', token)
        .select();
      
      if (error) {
        console.error('Erreur lors de la mise à jour du statut page_vue:', error);
        return null;
      }
      
      return data;
    } catch (error) {
      console.error('Exception dans trackScoreView:', error);
      return null;
    }
  },
  async trackPageView(token: string) {
    try {
      
      // Vérifier d'abord si le prospect existe avec ce token
      const { data: prospect, error: checkError } = await supabase
        .from('prospects')
        .select('id, token, page_vue')
        .eq('token', token)
        .single();
      
      if (checkError) {
        console.error('Erreur lors de la vérification du token:', checkError);
        throw checkError;
      }
      
      if (!prospect) {
        console.error('Aucun prospect trouvé avec ce token:', token);
        return;
      }
      
      
      // Mise à jour avec date actuelle
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('prospects')
        .update({ 
          page_vue: true, 
          updated_at: now
        })
        .eq('token', token)
        .select();
      
      if (error) {
        console.error('Erreur lors de la mise à jour du statut page_vue:', error);
        throw error;
      }
      
      return data;
    } catch (error) {
      console.error('Exception dans trackPageView:', error);
      // Ne pas propager l'erreur pour éviter de bloquer la navigation
      return null;
    }
  },

  async trackQuizStart(token: string) {
    try {
      // Vérifier si le prospect existe et récupérer son état actuel
      const { data: prospect, error: prospectError } = await supabase
        .from('prospects')
        .select('*')
        .eq('token', token)
        .single();
      
      if (prospectError) throw prospectError;
      
      // Ne mettre à jour que si nécessaire (pas déjà commencé)
      if (prospect && !prospect.quiz_commence) {
        const { error } = await supabase
          .from('prospects')
          .update({ quiz_commence: true, quiz_en_cours: false, quiz_fait: false })
          .eq('token', token);
        
        if (error) throw error;
      }
      
      return prospect;
    } catch (error) {
      console.error('Error tracking quiz start:', error);
      throw error;
    }
  },

  async trackQuizComplete(token: string, score: number) {
    try {
      
      // Vérifier d'abord si le prospect existe avec ce token
      const { data: prospect, error: checkError } = await supabase
        .from('prospects')
        .select('id, token, quiz_fait')
        .eq('token', token)
        .single();
      
      if (checkError) {
        console.error('Erreur lors de la vérification du token pour quiz complete:', checkError);
        return null;
      }
      
      if (!prospect) {
        console.error('Aucun prospect trouvé avec ce token pour quiz complete:', token);
        return null;
      }
      
      
      // Mise à jour avec date actuelle
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('prospects')
        .update({ 
          quiz_fait: true,
          quiz_en_cours: false,
          score: score,
          updated_at: now
        })
        .eq('token', token)
        .select();
      
      if (error) {
        console.error('Erreur lors de la mise à jour quiz complete:', error);
        throw error;
      }
      
      return data;
    } catch (error) {
      console.error('Exception dans trackQuizComplete:', error);
      // On propage l'erreur car c'est une action critique
      throw error;
    }
  },

  async trackRdvPris(token: string) {
    try {
      const { error } = await supabase
        .from('prospects')
        .update({ rdv_pris: true })
        .eq('token', token);

      if (error) throw error;
    } catch (error) {
      console.error('Error tracking RDV:', error);
      throw error;
    }
  },

  async saveQuizResponse(token: string, response: { question_id: string; reponse: string; score_partiel: number }, responseNumber: number) {
    try {
      // Utiliser la procédure stockée pour garantir la cohérence des données
      const { data, error } = await supabase.rpc('save_quiz_response', {
        p_token: token,
        p_question_id: response.question_id,
        p_reponse: response.reponse,
        p_score_partiel: response.score_partiel,
        p_response_number: responseNumber
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error saving quiz response:', error);
      throw error;
    }
  },

  // Méthode de compatibilité pour l'ancien code
  async saveQuizResponses(token: string, responses: { question_id: string; reponse: string; score_partiel: number }[]) {
    try {
      // Insérer toutes les réponses
      const { error } = await supabase
        .from('reponses_quiz')
        .insert(responses.map(r => ({ ...r, token })));

      if (error) throw error;
      
      // Mettre à jour le statut en fonction du nombre total de réponses
      const { data: allResponses, error: countError } = await supabase
        .from('reponses_quiz')
        .select('question_id')
        .eq('token', token);
      
      if (countError) throw countError;
      
      const responseCount = allResponses?.length || 0;
      
      // Appeler la procédure stockée avec la dernière réponse et le compte total
      if (responses.length > 0 && responseCount > 0) {
        await this.saveQuizResponse(token, responses[responses.length - 1], responseCount);
      }
    } catch (error) {
      console.error('Error saving quiz responses:', error);
      throw error;
    }
  },
  
  async getQuizState(token: string) {
    try {
      // Récupérer l'état actuel du quiz
      const { data: prospect, error: prospectError } = await supabase
        .from('prospects')
        .select('*')
        .eq('token', token)
        .single();
      
      if (prospectError) throw prospectError;
      
      // Récupérer les réponses existantes
      const { data: responses, error: responsesError } = await supabase
        .from('reponses_quiz')
        .select('*')
        .eq('token', token)
        .order('question_id', { ascending: true });
      
      if (responsesError) throw responsesError;
      
      return {
        prospect,
        responses: responses || [],
        responseCount: responses?.length || 0,
        isComplete: prospect?.quiz_fait || false
      };
    } catch (error) {
      console.error('Error getting quiz state:', error);
      throw error;
    }
  }
};